<template>
    <div class="add customer__form modal modal--default">
        <div class="modal__panel" style="min-height: 500px;">
            <div class="modal__logo-wrapper">
                <div class="modal__logo"></div>
            </div>
            <div class="add__new">{{ $t(`leads.${"importLeads"}`) }}</div>
            <div class="modal__main-icon modal__main-icon--leads"></div>
            <div class="modal__close" @click="closeModal()"></div>

            <div class="form">
                <Loading v-if="loading" />
                <div v-else class="add__form import__form">
                    <div v-if="errorRows && errorRows.length == 0">
                        <div class="form__row team-row" v-if="userTeams && userTeams.length > 0">
                            <VSelect class="select select--default" :class="{ 'select--error': teamHasError }"
                                :options="userTeams" label="name" :reduce="(team) => team.id"
                                @update:modelValue="getFiltersData($event)" :placeholder="$t('settingsPage.selectTeamPlaceholder')"
                                v-model="teamId" />
                            <div class="form__error" v-if="teamHasError">
                                {{ $t(`addTeamModal.${"teamIdErrorMsg"}`) }}
                            </div>
                        </div>
                        <div class="form__row team-row" v-if="userTeams && userTeams.length > 0 && teamId">
                            <VSelect class="select select--default" :options="agents" :reduce="(agent) => agent.id"
                                :getOptionLabel="(agent) => `${agent.first_name} ${agent.last_name}`"
                                :placeholder="$t('policyProvision.agent')" v-model="agentId" />
                        </div>

                        <div class="form__row" v-if="userTeams && userTeams.length > 0 && teamId">
                            <DropFile @input="handleFileUpload" />
                        </div>

                        <div class="form__row sample">
                            <a href="/leads_import_sample.xlsx" class="btn">{{ $t(`settingsPage.${"downloadSample"}`)
                                }}</a>
                        </div>
                    </div>
                    <div v-else class="error-table-wrapper">
                        <h4 class="form__title">{{ $t(`leads.${"importErrors"}`) }} ({{ errorRows.length }})</h4>
                        <table>
                            <thead>
                                <tr>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"row"}`) }}</th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"name"}`) }}</th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"type"}`) }}</th>
                                    <th>Email</th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"phone"}`) }}</th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"zipCode"}`) }}</th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"city"}`) }}</th>
                                    <th>{{ $t(`accountDetails.${"customerLeadsTable"}.${"header"}.${"category"}`) }}
                                    </th>
                                    <th>{{ $t(`accountDetails.${"customerLeadsTable"}.${"header"}.${"referal"}`) }}</th>
                                    <th>{{ $t(`accountDetails.${"customerLeadsTable"}.${"header"}.${"referalNote"}`) }}
                                    </th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"deadline"}`) }}</th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"status"}`) }}</th>
                                    <th>{{ $t(`leads.${"tableHeader"}.${"text"}`) }}</th>
                                    <th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, index) in errorRows" :key="index">
                                    <td>{{ row.row }}</td>
                                    <td>{{ row.vorname }} {{ row.nachname }}</td>
                                    <td>{{ row.typ }}</td>
                                    <td>{{ row.email }}</td>
                                    <td>{{ row.telefon }}</td>
                                    <td>{{ row.plz }}</td>
                                    <td>{{ row.stadt }}</td>
                                    <td>{{ row.kategorie }}</td>
                                    <td>{{ row.verweisung }}</td>
                                    <td>{{ row.empfehlungshinweis }}</td>
                                    <td>{{ row.frist }}</td>
                                    <td>{{ row.status }}</td>
                                    <td>{{ row.text }}</td>
                                    <td class="error-message">{{ row.error }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="isRequesting"
                    class="modal__button modal__button--submit button button--default button--loading">
                </div>
                <div v-else>
                    <div v-if="downloadOrClose">
                        <button class="modal__button modal__button--submit button button--default"
                            @click="downloadErrorRows(errorRows)" style="right: 240px;">
                            <span>Download</span>
                        </button>
                        <button class="modal__button modal__button--submit button button--default"
                            @click="closeModal()">
                            <span>{{ $t(`buttons.${"cancelBtn"}`) }}</span>
                        </button>
                    </div>

                    <button v-else-if="canImportLeads" class="modal__button modal__button--submit button button--default"
                        @click="importLeads()">
                        <span>{{ $t(`leads.${"import"}`) }}</span>
                    </button>
                </div>

            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import DropFile from './DropFile.vue';
import api from "@/api";
import * as XLSX from '@e965/xlsx';
import Loading from "@/partials/Loading.vue";
import { useI18n } from 'vue-i18n';   

// eslint-disable-next-line no-unused-vars
const { t } = useI18n();

defineOptions({
    name: "LeadImportModal"
});

const store = useStore();
const route = useRoute();

// Reactive Local State Data
const teamId = ref(null);
const agentId = ref(null);
const loading = ref(false);
const teamHasError = ref(false);
const isRequesting = ref(false);
const uploadedFile = ref(null);
const errorRows = ref([]);
const downloadOrClose = ref(false);

// Watchers
watch(teamId, (newValue) => {
    teamHasError.value = !newValue;
});

// Computed Mappings (Replaces mapGetters)
const searchLeadForms = computed(() => store.getters.searchLeadForms);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);

const userTeams = computed(() => {
    const authorizedIds = store.getters.authorizedTeamIds("customer_create");
    if (authorizedIds === null && isSuperAdmin.value) {
        return store.getters.teams || [];
    }
    return store.getters.authorizationMemberships
        .filter((membership) => authorizedIds.includes(membership.team.id))
        .map((membership) => membership.team);
});

const agents = computed(() => {
    if (!searchLeadForms.value || !searchLeadForms.value.agents) return [];
    return searchLeadForms.value.agents;
});

const canImportLeads = computed(() => {
    if (isSuperAdmin.value) return true;
    if (!teamId.value) return false;
    return store.getters.hasTeamPermission("customer_create", teamId.value);
});

// Methods
const closeModal = () => {
    store.commit("setShowLeadImportModal", false);
};

const handleFileUpload = (files) => {
    if (files && files.length > 0) {
        uploadedFile.value = files[0].file;
    }
};

const getFiltersData = async (selectedTeamId) => {
    agentId.value = null;
    loading.value = true;
    if (selectedTeamId != null) {
        await store.dispatch("getSearchLeadForms", selectedTeamId);
    } else {
        store.commit("setSearchLeadForms", {});
    }
    loading.value = false;
};

const importLeads = async () => {
    if (!teamId.value) {
        teamHasError.value = true;
        return;
    }

    if (!canImportLeads.value) {
        store.commit("setToast", {
            display: true,
            type: "error",
            message: t("noData.noPermissionMessage"),
        });
        return;
    }

    if (!uploadedFile.value) {
        store.commit("setToast", {
            display: true,
            type: "error",
            message: store.getters.t ? store.getters.t(`leadsImportMessages.noFile`) : "No file selected.",
        });
        return;
    }

    try {
        isRequesting.value = true;
        const formData = new FormData();
        formData.append('team_id', teamId.value);
        if (agentId.value) {
            formData.append('agent_id', agentId.value);
        }
        formData.append('file', uploadedFile.value);

        const response = await api({ requiresAuth: true }).post('/v1/import-lead-customers', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data && response.data.errors && response.data.errors.length > 0) {
            errorRows.value = response.data.errors;
            downloadOrClose.value = true;
        } else {
            store.commit("setToast", {
                display: true,
                type: "success",
                message: t("leadsImportMessages.leadsSuccessfullyImported"),
            });
            searchLeads();
            closeModal();
        }
    } catch (error) {
        store.commit("setToast", {
            display: true,
            type: "error",
            message: error.response?.data?.message || t("messages.failedToImportLeads"),
        });
    } finally {
        isRequesting.value = false;
    }
};

const searchLeads = async () => {
    const routeTeamId = Number(route.query.team_id);
    await store.dispatch("searchLeads", {
        page: 1,
        teamId: Number.isInteger(routeTeamId) && routeTeamId > 0 ? routeTeamId : null,
    });
};

onMounted(async () => {
    if (isSuperAdmin.value && !store.getters.teams.length) {
        await store.dispatch("getTeams");
    }

    const routeTeamId = Number(route.query.team_id);
    if (Number.isInteger(routeTeamId)
        && routeTeamId > 0
        && (isSuperAdmin.value || store.getters.hasTeamPermission("customer_create", routeTeamId))) {
        teamId.value = routeTeamId;
        await getFiltersData(routeTeamId);
    }
});

const downloadErrorRows = (rows) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");
    XLSX.writeFile(workbook, "Lead_Import_Errors.xlsx");
};
</script> 

<style lang="scss" scoped>
@use "../assets/scss/add-panel-modal";

.sample {
    margin-top: 30px;
}


.modal {
    .import__form {
        width: 100% !important;
        margin-top: 20px !important;
    }

    .form__row {
        justify-content: center;
    }

    &__panel {
        height: auto !important;
        width: 75%;
    }
}

.team-row {
    width: 30%;
    margin: 20px auto;

    flex-direction: column;

}

.form__error {
    position: initial !important;
    margin-top: 5px;
}

.error-table-wrapper {
    width: 100%;
    margin-top: 50px;
    overflow: auto;
    max-height: 19em;
}

.form__title {
    color: #FFA500;
    font-weight: normal;
    font-size: 14px;
    margin-top: 10px;
    margin-bottom: 20px;
    text-align: center;
}

table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin-top: 20px;
    border-spacing: 6px 0px;
    table-layout: fixed;
}

th,
td {
    padding: 12px 8px;
    text-align: left;
    background-color: #fff;
    box-shadow: 0px 0px 10px #0000000D;
    border-bottom: 1px solid #DADCDE;
    border-radius: 0px;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;

    &:nth-child(1) {
        width: 47px;
    }

    /* Row */
    &:nth-child(2) {
        width: 120px;
    }

    /* Name */
    &:nth-child(3) {
        width: 88px;
    }

    /* Type */
    // &:nth-child(4) { width: 150px; } /* Email */
    // &:nth-child(5) { width: 120px; } /* Phone */
    // &:nth-child(6) { width: 50px; }  /* PLZ */
    // &:nth-child(7) { width: 90px; } /* Stadt */
    // &:nth-child(8) { width: 100px; } /* Kategorie */
    //  &:nth-child(9) { width: 95px; } /* Verweisung */
    // &:nth-child(10) { width: 90px; } /* Frist */
    // &:nth-child(11) { width: 100px; } /* Status */
    // &:nth-child(12) { width: 120px; } /* Text */
    &:nth-child(14) {
        width: 200px;
        /* Error - wider column */
    }
}

td {
    letter-spacing: 0px;
    color: #7F8FA4;
}

tr:first-child th {
    border-radius: 8px 8px 0px 0px;
}

tr:last-child td {
    border-radius: 0px 0px 8px 8px;
    border: none;
}

th {
    color: #22DB8E;
    font-weight: normal;
    font-size: 14px;
}

tbody tr+tr td {
    margin-top: 15px;
}

tbody tr:hover td {
    background-color: #f1f1f1;
}

.error-message {
    white-space: normal !important;
    word-break: break-word;
    color: #d9534f;
    overflow: visible !important;
    text-overflow: initial !important;
} 

[data-theme="dark"] tr th,
[data-theme="dark"] tr td {
    background: var(--bg-17) !important;
}

[data-theme="dark"] tr td,
[data-theme="dark"] .form__title,
[data-theme="dark"] .sample a {
    color: white !important;
}
</style>
